'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2, Info } from 'lucide-react';

// Mock credentials — replace with real auth (Supabase, NextAuth, etc.)
const DEMO_EMAIL = 'seeker@svyatoshatalks.club';
const DEMO_PASSWORD = 'deepwater2026';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  name: string;
  email: string;
  password: string;
  terms: boolean;
}

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const router = useRouter();

  // Login form
  const {
    register: regLogin,
    handleSubmit: handleLogin,
    formState: { errors: loginErrors, isSubmitting: loginSubmitting },
    setError: setLoginError,
  } = useForm<LoginForm>();

  // Signup form
  const {
    register: regSignup,
    handleSubmit: handleSignup,
    formState: { errors: signupErrors, isSubmitting: signupSubmitting },
    watch: watchSignup,
  } = useForm<SignupForm>();

  const onLogin = async (data: LoginForm) => {
    // TODO: Replace with real auth — NextAuth signIn() or Supabase auth.signInWithPassword()
    await new Promise(r => setTimeout(r, 900));
    if (data.email === DEMO_EMAIL && data.password === DEMO_PASSWORD) {
      toast.success('Добро пожаловать обратно');
      router.push('/member-dashboard');
    } else {
      setLoginError('password', {
        message: `Неверные данные. Попробуй: ${DEMO_EMAIL} / ${DEMO_PASSWORD}`,
      });
    }
  };

  const onSignup = async (data: SignupForm) => {
    // TODO: Replace with real user creation — Supabase auth.signUp() or similar
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Аккаунт создан! Добро пожаловать.');
    router.push('/member-dashboard');
  };

  const passwordValue = watchSignup('password', '');

  return (
    <div className="w-full max-w-sm">
      {/* Tabs */}
      <div className="flex mb-8 p-1 rounded-xl" style={{ background: '#141414', border: '1px solid #2A2520' }}>
        {(['login', 'signup'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-500 transition-all duration-200 ${tab === t ? 'text-[#0A0A0A]' : 'text-[#7A7470]'}`}
            style={tab === t ? { background: '#F0EDE8', fontWeight: 500 } : { fontWeight: 500 }}
          >
            {t === 'login' ? 'Войти' : 'Регистрация'}
          </button>
        ))}
      </div>

      {/* Login */}
      {tab === 'login' && (
        <form onSubmit={handleLogin(onLogin)} className="space-y-5" noValidate>
          <div>
            <label className="block text-xs font-500 mb-2" style={{ color: '#7A7470', fontWeight: 500, letterSpacing: '0.02em' }}>
              Email
            </label>
            <input
              type="email"
              placeholder="ваш@email.com"
              {...regLogin('email', {
                required: 'Введите email',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Неверный формат' },
              })}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
              style={{
                background: '#141414',
                border: `1px solid ${loginErrors.email ? '#DC2626' : '#2A2520'}`,
                color: '#F0EDE8',
              }}
              onFocus={e => { e.target.style.borderColor = loginErrors.email ? '#DC2626' : '#C9A96E'; }}
              onBlur={e => { e.target.style.borderColor = loginErrors.email ? '#DC2626' : '#2A2520'; }}
            />
            {loginErrors.email && (
              <p className="text-red-500 text-xs mt-1.5">{loginErrors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-500 mb-2" style={{ color: '#7A7470', fontWeight: 500, letterSpacing: '0.02em' }}>
              Пароль
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...regLogin('password', { required: 'Введите пароль' })}
                className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-all duration-200"
                style={{
                  background: '#141414',
                  border: `1px solid ${loginErrors.password ? '#DC2626' : '#2A2520'}`,
                  color: '#F0EDE8',
                }}
                onFocus={e => { e.target.style.borderColor = loginErrors.password ? '#DC2626' : '#C9A96E'; }}
                onBlur={e => { e.target.style.borderColor = loginErrors.password ? '#DC2626' : '#2A2520'; }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                style={{ color: '#7A7470' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {loginErrors.password && (
              <p className="text-red-500 text-xs mt-1.5 leading-relaxed">{loginErrors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...regLogin('remember')}
                className="w-4 h-4 rounded"
                style={{ accentColor: '#C9A96E' }}
              />
              <span className="text-xs" style={{ color: '#7A7470' }}>Запомнить меня</span>
            </label>
            <button type="button" className="text-xs transition-colors" style={{ color: '#C9A96E' }}>
              Забыл пароль?
            </button>
          </div>

          <button
            type="submit"
            disabled={loginSubmitting}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-500 transition-all duration-200 active:scale-95 disabled:opacity-50"
            style={{ background: '#C9A96E', color: '#0A0A0A', fontWeight: 500 }}
          >
            {loginSubmitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                Войти
                <ArrowRight size={14} />
              </>
            )}
          </button>

          {/* Demo credentials box */}
          <div
            className="flex items-start gap-3 p-4 rounded-xl"
            style={{ background: 'rgba(201,169,110,0.06)', border: '1px solid rgba(201,169,110,0.15)' }}
          >
            <Info size={14} style={{ color: '#C9A96E', flexShrink: 0, marginTop: 1 }} />
            <div>
              <p className="text-xs font-500 mb-1" style={{ color: '#C9A96E', fontWeight: 500 }}>Демо-доступ</p>
              <p className="text-xs leading-relaxed" style={{ color: '#7A7470' }}>
                Email: <span style={{ color: '#F0EDE8' }}>{DEMO_EMAIL}</span><br />
                Пароль: <span style={{ color: '#F0EDE8' }}>{DEMO_PASSWORD}</span>
              </p>
            </div>
          </div>
        </form>
      )}

      {/* Signup */}
      {tab === 'signup' && (
        <form onSubmit={handleSignup(onSignup)} className="space-y-5" noValidate>
          <div>
            <label className="block text-xs font-500 mb-2" style={{ color: '#7A7470', fontWeight: 500, letterSpacing: '0.02em' }}>
              Имя
            </label>
            <input
              type="text"
              placeholder="Как тебя зовут"
              {...regSignup('name', { required: 'Введите имя' })}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
              style={{
                background: '#141414',
                border: `1px solid ${signupErrors.name ? '#DC2626' : '#2A2520'}`,
                color: '#F0EDE8',
              }}
              onFocus={e => { e.target.style.borderColor = '#C9A96E'; }}
              onBlur={e => { e.target.style.borderColor = signupErrors.name ? '#DC2626' : '#2A2520'; }}
            />
            {signupErrors.name && (
              <p className="text-red-500 text-xs mt-1.5">{signupErrors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-500 mb-2" style={{ color: '#7A7470', fontWeight: 500, letterSpacing: '0.02em' }}>
              Email
            </label>
            <input
              type="email"
              placeholder="ваш@email.com"
              {...regSignup('email', {
                required: 'Введите email',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Неверный формат' },
              })}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
              style={{
                background: '#141414',
                border: `1px solid ${signupErrors.email ? '#DC2626' : '#2A2520'}`,
                color: '#F0EDE8',
              }}
              onFocus={e => { e.target.style.borderColor = '#C9A96E'; }}
              onBlur={e => { e.target.style.borderColor = signupErrors.email ? '#DC2626' : '#2A2520'; }}
            />
            {signupErrors.email && (
              <p className="text-red-500 text-xs mt-1.5">{signupErrors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-500 mb-2" style={{ color: '#7A7470', fontWeight: 500, letterSpacing: '0.02em' }}>
              Пароль
            </label>
            <p className="text-xs mb-2" style={{ color: '#4A4440' }}>Минимум 8 символов</p>
            <div className="relative">
              <input
                type={showSignupPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...regSignup('password', {
                  required: 'Введите пароль',
                  minLength: { value: 8, message: 'Минимум 8 символов' },
                })}
                className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-all duration-200"
                style={{
                  background: '#141414',
                  border: `1px solid ${signupErrors.password ? '#DC2626' : '#2A2520'}`,
                  color: '#F0EDE8',
                }}
                onFocus={e => { e.target.style.borderColor = '#C9A96E'; }}
                onBlur={e => { e.target.style.borderColor = signupErrors.password ? '#DC2626' : '#2A2520'; }}
              />
              <button
                type="button"
                onClick={() => setShowSignupPassword(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                style={{ color: '#7A7470' }}
                aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
              >
                {showSignupPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signupErrors.password && (
              <p className="text-red-500 text-xs mt-1.5">{signupErrors.password.message}</p>
            )}
            {/* Password strength indicator */}
            {passwordValue && (
              <div className="flex gap-1 mt-2">
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className="h-1 flex-1 rounded-full transition-all duration-300"
                    style={{
                      background: passwordValue.length >= i * 4
                        ? i === 3 ? '#5A7A5A' : i === 2 ? '#C9A96E' : '#DC2626' :'#2A2520',
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...regSignup('terms', { required: 'Необходимо согласие' })}
                className="w-4 h-4 rounded mt-0.5 flex-shrink-0"
                style={{ accentColor: '#C9A96E' }}
              />
              <span className="text-xs leading-relaxed" style={{ color: '#7A7470' }}>
                Я принимаю{' '}
                <button type="button" className="underline" style={{ color: '#C9A96E' }}>условия использования</button>
                {' '}и{' '}
                <button type="button" className="underline" style={{ color: '#C9A96E' }}>политику конфиденциальности</button>
              </span>
            </label>
            {signupErrors.terms && (
              <p className="text-red-500 text-xs mt-1.5">{signupErrors.terms.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={signupSubmitting}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-500 transition-all duration-200 active:scale-95 disabled:opacity-50"
            style={{ background: '#C9A96E', color: '#0A0A0A', fontWeight: 500 }}
          >
            {signupSubmitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                Создать аккаунт
                <ArrowRight size={14} />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}